import { Link } from "react-router";
import type { Alert } from "@/types/models";
import SnoozeMenu from "./SnoozeMenu";

// Operational "needs attention" for the owner dashboard — low stock, overdue
// bills/invoices. Flock problems live in the "Flocks to act on" card above, so
// this panel deliberately shows only farm-level (non-flock) alerts to avoid
// listing the same flock twice. Renders nothing when there's nothing operational
// wrong. Each row links to its action page via `alert.link`; owners can snooze.
export default function NeedsAttentionPanel({
  alerts,
  error,
  onSnooze,
}: {
  alerts: Alert[];
  error?: boolean;
  onSnooze?: (alert: Alert, days?: number) => void;
}) {
  // Operational alerts only — flock-scoped ones are owned by the card.
  const operational = alerts.filter((a) => a.flock_id == null);

  if (operational.length === 0) {
    // On a real fetch failure, say so rather than rendering nothing — a blank panel
    // would read as a false "all clear" while something could actually need attention.
    if (error) {
      return (
        <div className="mb-6 card p-4 text-sm text-gray-500 dark:text-gray-400">
          Couldn&rsquo;t check what needs attention right now &mdash; try refreshing.
        </div>
      );
    }
    return null;
  }
  const critical = operational.filter((a) => a.severity === "critical").length;

  return (
    <div className="mb-6 card p-5 sm:p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">Needs attention</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {operational.length} {operational.length === 1 ? "item" : "items"}
          {critical > 0 && <> &middot; {critical} critical</>}
        </span>
      </div>
      <ul className="space-y-2">
        {operational.map((a, i) => (
          <AlertRow key={`${a.rule_id}-${i}`} alert={a} onSnooze={onSnooze} />
        ))}
      </ul>
    </div>
  );
}

function AlertRow({ alert, onSnooze }: { alert: Alert; onSnooze?: (alert: Alert, days?: number) => void }) {
  const dot = alert.severity === "critical" ? "bg-error-500" : "bg-warning-500";
  const body = (
    <div className="flex items-start gap-3 rounded-lg border border-gray-100 px-3 py-2 dark:border-gray-800/60">
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-white/90">{alert.title}</p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{alert.detail}</p>
      </div>
    </div>
  );
  return (
    <li className="flex items-start gap-1">
      <div className="min-w-0 flex-1">
        {alert.link ? (
          <Link to={alert.link} className="block rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.02]">
            {body}
          </Link>
        ) : (
          body
        )}
      </div>
      {/* Operational alerts are category-wide, so no indefinite snooze (backend-enforced). */}
      {onSnooze && <SnoozeMenu allowIndefinite={false} onPick={(days) => onSnooze(alert, days)} />}
    </li>
  );
}
